import { getActions } from '../../global';

import type { TauriNotificationClickPayload } from '../../types/tauri';

import { DEBUG } from '../../config';
import { MouseButton } from '../browser/windowEnvironment';
import { isInternalWebPage } from '../deepLinkParser';
import { trackTauriWindowFocus } from './windowFocus';

type DownloadFinishedEvent = {
  success: boolean;
  url: string;
};

let isSetup = false;
export default function setupTauriListeners() {
  if (isSetup) return;
  isSetup = true;

  trackTauriWindowFocus();

  // Disable default tauri context menu in production build
  if (!DEBUG) {
    document.addEventListener('contextmenu', (event) => {
      event.preventDefault();
    });
  }

  const eventPromise = import('@tauri-apps/api/event');
  eventPromise.then(({ listen }) => {
    listen<DownloadFinishedEvent>('download-finished', (event) => {
      if (event.payload.success) return;

      getActions().showNotification({
        message: { key: 'NativeDownloadFailed' },
      });
    });

    listen<TauriNotificationClickPayload>('notification-clicked', (event) => {
      const { chatId, messageId } = event.payload;

      getActions().focusMessage({
        chatId,
        messageId,
        shouldReplaceHistory: true,
      });
    });
  });

  // Disable Backspace handling as back navigation
  document.addEventListener('keydown', (event: KeyboardEvent) => {
    if (event.key === 'Backspace' || event.code === 'Backspace') {
      const target = event.target as HTMLElement | null;
      const isInputField = target?.tagName === 'INPUT'
        || target?.tagName === 'TEXTAREA'
        || target?.isContentEditable;

      if (!isInputField) {
        event.preventDefault();
      }
    }
  }, true);

  window.open = (url: string | URL | undefined, target?: string, features?: string) => {
    if (url) openLink(url);
    // eslint-disable-next-line no-null/no-null
    return null;
  };

  function handleClick(event: MouseEvent) {
    const target = event.target as HTMLElement | null;
    const anchor = target?.closest<HTMLAnchorElement>('a[href]');

    if (!anchor) return;

    if (anchor.target === '_blank'
      || event.ctrlKey
      || event.metaKey
      || event.button === MouseButton.Auxiliary
    ) {
      event.preventDefault();

      const href = anchor.getAttribute('href')!;
      openLink(href);
    }
  }

  document.addEventListener('click', handleClick);
  document.addEventListener('auxclick', handleClick);
}

async function openLink(url: string | URL) {
  try {
    const urlObject = url instanceof URL ? url : new URL(url, window.location.href);
    // Sharing our origin is not the same as being an app route. `/home` is the marketing site,
    // served from this very host, so it used to open as a second app window — one that renders an
    // ordinary web page with no way back to the chat. Worse, the last window to close is the URL the
    // app restores to, so once that window was the survivor the app reopened on the landing page
    // every time and only a force quit brought the chat back.
    const href = urlObject.toString();
    if (window.location.origin === urlObject.origin && !isInternalWebPage(href)) {
      await window.tauri.openNewWindow(href);
    } else {
      const shellPlugin = await import('@tauri-apps/plugin-shell');
      await shellPlugin.open(href);
    }
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error('Failed to open link:', url, e);
  }
}
